import { LOCAL_LAYOUT_STORAGE_KEY } from "../types/constants";
import * as logger from "common/logger";

// Per-user, per-browser persistence of a report's saved layouts (named views).
// Used by the embedded dashboard report so a user can save several views
// (columns, filters, sort, grouping, summary, metrics) and switch between them
// without creating a server-side saved report.
//
// Storage shape (keyed by reportId):
//   { activeName: string|null,
//     layouts: { [name]: { specification, configuration, savedAt } } }

const withErrorHandling = (operation, fn, fallback) => {
  try {
    return fn();
  } catch (error) {
    logger.info(operation, error);
    return fallback;
  }
};

const readBag = () =>
  withErrorHandling(
    "Failed to read local report layouts",
    () => JSON.parse(localStorage.getItem(LOCAL_LAYOUT_STORAGE_KEY) || "{}"),
    {}
  );

const writeBag = (bag) =>
  withErrorHandling(
    "Failed to write local report layouts",
    () => localStorage.setItem(LOCAL_LAYOUT_STORAGE_KEY, JSON.stringify(bag)),
    undefined
  );

const readState = (reportId) => {
  const entry = readBag()[reportId];
  if (!entry) {
    return { activeName: null, layouts: {} };
  }
  return {
    activeName: entry.activeName ?? null,
    layouts: entry.layouts ?? {},
  };
};

const writeState = (reportId, state) => {
  if (!reportId) return;
  const bag = readBag();
  bag[reportId] = { ...state };
  writeBag(bag);
};

// For the Layouts menu: the active view name (or null = default) + saved names.
export const loadLayoutNames = (reportId) => {
  if (!reportId) return { activeName: null, names: [] };
  const { activeName, layouts } = readState(reportId);
  return { activeName, names: Object.keys(layouts) };
};

// For the report load path: the layout to apply (null = company default).
export const getActiveLayout = (reportId) => {
  if (!reportId) return null;
  const { activeName, layouts } = readState(reportId);
  if (!activeName) return null;
  const layout = layouts[activeName];
  return layout?.specification ? layout : null;
};

// Create or overwrite a named layout and make it active.
export const saveNamedLayout = (
  reportId,
  name,
  { specification, configuration }
) => {
  if (!reportId || !name || !specification) return;
  const state = readState(reportId);
  state.layouts = {
    ...state.layouts,
    [name]: { specification, configuration, savedAt: new Date().toISOString() },
  };
  state.activeName = name;
  writeState(reportId, state);
};

// Switch the active layout (pass null for the company default).
export const setActiveLayout = (reportId, name) => {
  const state = readState(reportId);
  state.activeName = name && state.layouts[name] ? name : null;
  writeState(reportId, state);
};

export const deleteNamedLayout = (reportId, name) => {
  const state = readState(reportId);
  if (name in state.layouts) {
    const layouts = { ...state.layouts };
    delete layouts[name];
    state.layouts = layouts;
    if (state.activeName === name) state.activeName = null;
    writeState(reportId, state);
  }
};
